<template>
  <div class="processor-report">
    <ReportFormat @show_result="show_result" :is_table_show="false" :isLoading="loading">
      <template #report-form>
        <div class="vx-row pt-5 px-5">
          <div class="vx-col w-full lg:w-5/12 md:w-1/2">
            <div class="vx-row mb-2 items-center">
              <div class="vx-col w-2/5 text-right">
                <h6>Reporting Year</h6>
              </div>
              <div class="vx-col w-3/5">
                <select-2
                  autocomplete
                  class="w-full"
                  :value="year"
                  @input="item => (year = item && item.value)"
                  name="Reporting Year"
                  placeholder="Select Year"
                  :options="yearOptions"
                  v-validate="'required'"
                />
                <span class="text-danger text-sm" v-show="errors.has('Reporting Year')">{{ errors.first('Reporting Year') }}</span>
              </div>
            </div>
          </div>

          <div class="vx-col w-full lg:w-5/12 md:w-1/2">
            <div class="vx-row mb-2 items-center">
              <div class="vx-col w-2/5 text-right">
                <h6>Reporting Month</h6>
              </div>
              <div class="vx-col w-3/5">
                <select-2
                  autocomplete
                  class="w-full"
                  :value="month"
                  @input="item => (month = item && item.value)"
                  name="Reporting Month"
                  placeholder="Please Select Month"
                  :options="monthOptions"
                  v-validate="'required'"
                />
                <span class="text-danger text-sm" v-show="errors.has('Reporting Month')">{{ errors.first('Reporting Month') }}</span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </ReportFormat>

    <ProcessorReportData ref="report" @download_excel_report="exportReport" v-if="Object.keys(reports_data).length" :year="year" :month="month" />
  </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex'
import XLSX from 'xlsx'
import moment from 'moment'
import { weightMixins } from '@/utils/weightType'
import ReportFormat from '@/views/reports/components/ReportFormat.vue'
import ProcessorReportData from '@/views/reports/components/ProcessorReportData.vue'
import Select2 from '@/components/custom/form-elements/Select2.vue'

export default {
  name: 'ProcessorReport',

  components: {
    ReportFormat,
    ProcessorReportData,
    Select2
  },

  data() {
    return {
      loading: false,
      page: 1,
      limit: 0,
      search: '',
      yearOptions: this.getYearsOptions(),
      year: moment().year(),
      monthOptions: this.getMonthOptions(),
      month: moment().month() + 1
    }
  },

  mixins: [weightMixins],

  methods: {
    moment,
    ...mapMutations('report', { set_state: 'SET_STATE' }),

    getYearsOptions() {
      const years = []
      let startYear = moment('2019', 'YYYY').format('YYYY')
      const lastYear = moment().format('YYYY')

      while (startYear <= lastYear) {
        years.push({ label: startYear, value: startYear })
        startYear++
      }

      return years
    },

    getMonthOptions() {
      return moment.months().reduce((acc, item, index) => {
        const tempObj = {}
        tempObj.value = index + 1
        tempObj.label = item

        acc.push(tempObj)

        return acc
      }, [])
    },

    // fetch processor report
    async show_result() {
      this.loading = true
      const payload = {
        year: this.year,
        month: this.month
      }

      this.$store
        .dispatch('report/getProcessorReport', payload)
        .then(() => {
          this.loading = false
        })
        .catch(err => {
          const { message } = err
          this.loading = false
          this.$vs.notify({
            title: 'Error',
            text: message,
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            color: 'danger'
          })
        })
    },

    // export report in excel
    exportReport() {
      this.generating = true

      const wb = XLSX.utils.book_new()

      // general details
      const generalDetail = [
        ['OMMA License ID/Number', this.reports_data.parentUser.omma_license_number],
        ['Role', this.reports_data.parentUser.role.role_name],
        ['Email', this.reports_data.parentUser.email],
        ['Phone Number', this.reports_data.parentUser.mobile],
        ['Reporting Year', this.year],
        ['Reporting Month', moment(this.month, 'M').format('MMMM')]
      ]
      const ws = XLSX.utils.json_to_sheet(generalDetail, { skipHeader: true })

      // The amount of marijuana on hand at the beginning of the reporting period.
      XLSX.utils.sheet_add_aoa(ws, [['The amount of marijuana on hand at the beginning of the reporting period.']], { origin: 'A8' })
      let amountMarijuanaAtBeginning = XLSX.utils.table_to_sheet(this.$refs.report.$refs.amountMarijuanaAtBeginning.$el)
      amountMarijuanaAtBeginning = XLSX.utils.sheet_to_json(amountMarijuanaAtBeginning)
      XLSX.utils.sheet_add_json(ws, amountMarijuanaAtBeginning, { origin: 'A9' })

      // The amount of marijuana purchased from grower licensees.
      XLSX.utils.sheet_add_aoa(ws, [['The amount of marijuana purchased from grower licensees.']], { origin: 'A24' })
      let amountMarijuanaPurchasedFromGrower = XLSX.utils.table_to_sheet(this.$refs.report.$refs.amountMarijuanaPurchasedFromGrower.$el)
      amountMarijuanaPurchasedFromGrower = XLSX.utils.sheet_to_json(amountMarijuanaPurchasedFromGrower)
      XLSX.utils.sheet_add_json(ws, amountMarijuanaPurchasedFromGrower, { origin: 'A25' })

      // The amount of marijuana purchased from processor licensees.
      XLSX.utils.sheet_add_aoa(ws, [['The amount of marijuana purchased from processor licensees.']], { origin: 'A32' })
      let amountMarijuanaPurchasedFromProcessor = XLSX.utils.table_to_sheet(this.$refs.report.$refs.amountMarijuanaPurchasedFromProcessor.$el)
      amountMarijuanaPurchasedFromProcessor = XLSX.utils.sheet_to_json(amountMarijuanaPurchasedFromProcessor)
      XLSX.utils.sheet_add_json(ws, amountMarijuanaPurchasedFromProcessor, { origin: 'A33' })

      // The amount of medical marijuana manufactured or processed.
      XLSX.utils.sheet_add_aoa(ws, [['The amount of medical marijuana manufactured or processed.']], { origin: 'A48' })
      let amountMarijuanaManufactured = XLSX.utils.table_to_sheet(this.$refs.report.$refs.amountMarijuanaManufactured.$el)
      amountMarijuanaManufactured = XLSX.utils.sheet_to_json(amountMarijuanaManufactured)
      XLSX.utils.sheet_add_json(ws, amountMarijuanaManufactured, { origin: 'A49' })

      // The amount of marijuana sold to dispensary.
      XLSX.utils.sheet_add_aoa(ws, [['The amount of marijuana sold to dispensary.']], { origin: 'A64' })
      let amountMarijuanaSoldToDispensary = XLSX.utils.table_to_sheet(this.$refs.report.$refs.amountMarijuanaSoldToDispensary.$el)
      amountMarijuanaSoldToDispensary = XLSX.utils.sheet_to_json(amountMarijuanaSoldToDispensary)
      XLSX.utils.sheet_add_json(ws, amountMarijuanaSoldToDispensary, { origin: 'A65' })

      // The amount of marijuana sold to processor.
      XLSX.utils.sheet_add_aoa(ws, [['The amount of marijuana sold to processor.']], { origin: 'A80' })
      let amountMarijuanaSoldToProcessor = XLSX.utils.table_to_sheet(this.$refs.report.$refs.amountMarijuanaSoldToProcessor.$el)
      amountMarijuanaSoldToProcessor = XLSX.utils.sheet_to_json(amountMarijuanaSoldToProcessor)
      XLSX.utils.sheet_add_json(ws, amountMarijuanaSoldToProcessor, { origin: 'A81' })

      // The amount of marijuana sold to researcher.
      XLSX.utils.sheet_add_aoa(ws, [['The amount of marijuana sold to researcher.']], { origin: 'A96' })
      let amountMarijuanaSoldToResearcher = XLSX.utils.table_to_sheet(this.$refs.report.$refs.amountMarijuanaSoldToResearcher.$el)
      amountMarijuanaSoldToResearcher = XLSX.utils.sheet_to_json(amountMarijuanaSoldToResearcher)
      XLSX.utils.sheet_add_json(ws, amountMarijuanaSoldToResearcher, { origin: 'A97' })

      // The amount of marijuana sold total.
      XLSX.utils.sheet_add_aoa(ws, [['The amount of marijuana sold total.']], { origin: 'A112' })
      let amountMarijuanaSold = XLSX.utils.table_to_sheet(this.$refs.report.$refs.amountMarijuanaSold.$el)
      amountMarijuanaSold = XLSX.utils.sheet_to_json(amountMarijuanaSold)
      XLSX.utils.sheet_add_json(ws, amountMarijuanaSold, { origin: 'A113' })

      // The amount of marijuana waste in pounds.
      XLSX.utils.sheet_add_aoa(ws, [['The amount of marijuana waste in pounds.']], { origin: 'A128' })
      XLSX.utils.sheet_add_aoa(ws, [[weightMixins.methods.mathRound(this.reports_data.section9, 3)]], { origin: 'A129', skipHeader: true })

      // The amount of marijuana transported to other licensed locations during this reporting month.
      XLSX.utils.sheet_add_aoa(ws, [['The amount of marijuana transported to other licensed locations during this reporting month.']], { origin: 'A131' })
      let amountMarijuanaTransported = XLSX.utils.table_to_sheet(this.$refs.report.$refs.amountMarijuanaTransported.$el)
      amountMarijuanaTransported = XLSX.utils.sheet_to_json(amountMarijuanaTransported)
      XLSX.utils.sheet_add_json(ws, amountMarijuanaTransported, { origin: 'A132' })

      // The amount of marijuana transported to your business during this reporting month.
      XLSX.utils.sheet_add_aoa(ws, [['The amount of marijuana transported to your business during this reporting month.']], { origin: 'A147' })
      let amountMarijuanaTransportedToBussiness = XLSX.utils.table_to_sheet(this.$refs.report.$refs.amountMarijuanaTransportedToBussiness.$el)
      amountMarijuanaTransportedToBussiness = XLSX.utils.sheet_to_json(amountMarijuanaTransportedToBussiness)
      XLSX.utils.sheet_add_json(ws, amountMarijuanaTransportedToBussiness, { origin: 'A148' })

      // All transporter IDs that have transported for your business this reporting month.
      XLSX.utils.sheet_add_aoa(ws, [['All transporter IDs that have transported for your business this reporting month.']], { origin: 'A163' })
      let marijuanaTransporter = XLSX.utils.table_to_sheet(this.$refs.report.$refs.marijuanaTransporter.$el)
      marijuanaTransporter = XLSX.utils.sheet_to_json(marijuanaTransporter)
      XLSX.utils.sheet_add_json(ws, marijuanaTransporter, { origin: 'A164', skipHeader: true })

      // The amount of marijuana on hand at the end of the reporting period.
      XLSX.utils.sheet_add_aoa(ws, [['The amount of marijuana on hand at the end of the reporting period.']], { origin: `A${164 + this.reports_data.section12.transporters.length + 1}` })
      let amountMarijuanaAtEnd = XLSX.utils.table_to_sheet(this.$refs.report.$refs.amountMarijuanaAtEnd.$el)
      amountMarijuanaAtEnd = XLSX.utils.sheet_to_json(amountMarijuanaAtEnd)
      XLSX.utils.sheet_add_json(ws, amountMarijuanaAtEnd, { origin: `A${164 + this.reports_data.section12.transporters.length + 2}` })

      const mergedCells = [
        { s: { r: 7, c: 0 }, e: { r: 7, c: 2 } },
        { s: { r: 23, c: 0 }, e: { r: 23, c: 1 } },
        { s: { r: 31, c: 0 }, e: { r: 31, c: 2 } },
        { s: { r: 47, c: 0 }, e: { r: 47, c: 2 } },
        { s: { r: 63, c: 0 }, e: { r: 63, c: 2 } },
        { s: { r: 79, c: 0 }, e: { r: 79, c: 2 } },
        { s: { r: 95, c: 0 }, e: { r: 95, c: 2 } },
        { s: { r: 111, c: 0 }, e: { r: 111, c: 2 } },
        { s: { r: 130, c: 0 }, e: { r: 130, c: 2 } },
        { s: { r: 146, c: 0 }, e: { r: 146, c: 2 } },
        { s: { r: 163 + this.reports_data.section12.transporters.length + 1, c: 0 }, e: { r: 163 + this.reports_data.section12.transporters.length + 1, c: 2 } }
      ]

      ws['!merges'] = mergedCells

      XLSX.utils.book_append_sheet(wb, ws, 'Sheet 1')
      XLSX.writeFile(wb, 'ProcessorReport.xlsx')

      this.generating = false
    }
  },

  computed: {
    ...mapState('report', ['reports_data'])
  },

  filters: {
    //weightFilter rounded conversation
    weightFilter(value) {
      return weightMixins.methods.mathRound(value || 0, 3)
    }
  },

  async beforeDestroy() {
    await this.set_state({ action: 'reports_data', data: [] })
  }
}
</script>
